// RowDrawer.tsx
import React from 'react'
import { Drawer, Box, Stack, Typography, IconButton, Divider, Grid, TextField, SxProps } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { Row } from './taskTypes'
import RichTextField from 'components/ContractDialog/MuiRichTextField'
import { CREATIVE_FIELDS } from './CREATIVE_FIELDS'
import CreativeField from './CreativeField'

type ExtraFieldDef = { key: 'text1' | 'text2' | 'text3' | 'text4'; label: string }

export default function RowDrawer({
  open,
  onClose,
  row,
  onUpdate,
  denseInputSx,
  extraFields,
  // ✅ NEW
  headerExtras,
  containerEl,
  drawerWidth,
}: {
  open: boolean
  onClose: () => void
  row: Row | null
  onUpdate: (patch: Partial<Row>) => void
  denseInputSx: SxProps
  extraFields?: readonly { key: keyof Row; label: string }[]
  // ✅ NEW
  headerExtras?: React.ReactNode
  containerEl?: HTMLElement | null
  drawerWidth?: number
}) {
  const { pinned } = useLayoutContext()
  // local desc
  const [desc, setDesc] = React.useState<string>(row?.addtlNotes ?? '')
  React.useEffect(() => {
    if (open && row) setDesc(row.addtlNotes ?? '')
  }, [open, row?.addtlNotes])

  const debounceRef = React.useRef<number | null>(null)
  const commitDebounced = React.useCallback(
    (next: string) => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
      debounceRef.current = window.setTimeout(() => onUpdate({ addtlNotes: next }), 300)
    },
    [onUpdate]
  )
  React.useEffect(() => () => debounceRef.current && window.clearTimeout(debounceRef.current), [])

  const handleClose = () => {
    if (row) onUpdate({ addtlNotes: desc })
    onClose()
  }

  const showExtras = row?.platform === 'Google' && row?.channel === 'Search'
  const fields = extraFields ?? []
  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={open && !!row}
      onClose={handleClose}
      hideBackdrop
      ModalProps={{
        keepMounted: true,
        disableAutoFocus: true,
        disableRestoreFocus: true,
        disableEnforceFocus: true,
        container: containerEl || undefined,
      }}
      PaperProps={{
        sx: theme => ({
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          width: `${drawerWidth}px`,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: row?.kind === 'prod' ? 'background.toolbar' : 'text.hightlight',
          boxShadow: 'none',
          borderLeft: 1,
          borderColor: 'divider',
          borderTopRightRadius: theme.shape.borderRadius,
          borderBottomRightRadius: theme.shape.borderRadius,
        }),
      }}
      sx={{ zIndex: 1301 }}
    >
      <Box
        sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography variant="h6" sx={{ flex: 1 }} noWrap>
          {row?.name ? row?.name : 'Edit Task'}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      {/* ✅ NEW: headerExtras area */}
      {headerExtras ? <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>{headerExtras}</Box> : null}
      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Stack spacing={2}>
          <RichTextField
            key={row ? (row as any).SK ?? (row as any).id ?? 'desc' : 'desc'}
            // content={(desc ?? row?.addtlNotes ?? '').trim() || '<p></p>'}
            content={(desc ?? row?.addtlNotes ?? '').trim() || ''}
            onContentChange={(html: string) => {
              setDesc(html)
              commitDebounced(html)
            }}
            onBlur={() => row && onUpdate({ addtlNotes: desc })}
          />
          {row?.creative?.typeKey &&
            (() => {
              const typeKey = row.creative!.typeKey as keyof typeof CREATIVE_FIELDS
              const pretty = String(typeKey)
                .replace(/([A-Z])/g, ' $1')
                .trim()

              const cfg = CREATIVE_FIELDS[typeKey] ?? {}
              const entries = Object.entries(cfg)
              if (!entries.length) return null

              const values = row.creative?.values ?? {}
              const onFieldChange = (id: string, val: any) => {
                onUpdate({
                  creative: {
                    typeKey,
                    values: { ...values, [id]: val },
                  },
                } as const)
              }

              return (
                <>
                  <Divider textAlign="left">{pretty} — Extra Fields</Divider>
                  <Grid container spacing={1} columns={12} sx={{ mt: 1 }}>
                    {entries.map(([id, config]) => (
                      <Grid item xs={12} md={6} key={id}>
                        <CreativeField
                          id={id}
                          config={config}
                          value={values[id]}
                          onChange={v => onFieldChange(id, v)}
                          denseInputSx={denseInputSx}
                          containerEl={containerEl}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )
            })()}

          {showExtras && row && (
            <>
              <Divider textAlign="left">Google Search — Extra Fields</Divider>
              <Grid container spacing={1} columns={12}>
                {fields.map(({ key, label }) => (
                  <Grid item xs={12} md={6} key={String(key)}>
                    <TextField
                      label={label}
                      value={(row as any)[key] ?? ''}
                      onChange={e => onUpdate({ [key]: e.target.value } as Partial<Row>)}
                      size="small"
                      sx={denseInputSx}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Stack>
      </Box>
      {/* <Box sx={{ p: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end', borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={handleClose}>Close</Button>
      </Box> */}
    </Drawer>
  )
}
